import { col, type Shape, WELL_RATE_LIMITS } from './base.ts';

export const STRING_SENSOR_COMMON: Shape = [
  col('liftType', 'unitless', 'Artificial lift type.'),
  col('casingHeadPressure', 'pressureAbsolute', 'Casing head pressure.', { suffix: 'casingHeadPress' }),
  col('wellHeadPressure', 'pressureAbsolute', 'Flowing wellhead pressure.', { suffix: 'wellHeadPress' }),
  col('wellHeadTemperature', 'temperature', 'Flowing wellhead temperature.', { suffix: 'wellHeadTemp' }),
  col('annulusPressure', 'pressureAbsolute', 'Annulus pressure.', { suffix: 'annulusPress' }),
  col('chokeOpening', 'fraction', 'Production choke opening.'),
  col('chokeSize', 'chokeSize', 'Choke size setting.'),
  col('flowLinePressure', 'pressureAbsolute', 'Flowline pressure.', { suffix: 'flowLinePress' }),
  col('flowLineTemperature', 'temperature', 'Flowline temperature.', { suffix: 'flowLineTemp' }),
  col('bottomHolePressure', 'pressureAbsolute', 'Bottom-hole flowing pressure.', { suffix: 'bottomHolePress' }),
  col('bottomHoleTemperature', 'temperature', 'Bottom-hole temperature.', { suffix: 'bottomHoleTemp' }),
];

/**
 * Shared per-string operating-limit cluster common to both `EspLimits`
 * and `GasLiftLimits`: the un-prefixed rate-limit thresholds plus
 * the flow-assurance / stability risk flags. The three rate limits resolve as
 * TimeSeries references; the five risk columns are boolean scalars (the engine
 * type is derived from the parsed schema at construction, so a shape may mix
 * both kinds). Composed by spread into `ESP_LIMITS` / `GASLIFT_LIMITS`. Bare
 * canonical columns (`suffix === canonical`, `prefix:''`).
 */
export const STRING_LIMIT_COMMON: Shape = [
  ...WELL_RATE_LIMITS,
  col('waxRisk', 'unitless', 'Wax deposition risk flag for this well string.'),
  col('hydrateRisk', 'unitless', 'Hydrate formation risk flag for this well string.'),
  col('asphalteneRisk', 'unitless', 'Asphaltene deposition risk flag for this well string.'),
  col('pe5StabilityCriteria', 'unitless', 'PE5 flow-stability criteria threshold for this well string.'),
  col('fluidVelocity', 'velocity', 'Fluid velocity limit / reference flag for this well string.'),
];

/**
 * Model-calculated ESP performance outputs on `ESPCalculations_String_Agent`
 * (WF12): calculation status, motor efficiency/load, pump head and
 * average rate. All columns are already canonical-cased on the source — no
 * `esp` prefix — so this is a bare passthrough (`suffix === canonical`,
 * `prefix:''`). Each is a TimeSeries reference.
 */
export const ESP_CALCULATIONS: Shape = [
  col('espqlAnalysisStatus', 'unitless', 'Status of the ESP rate calculation.'),
  col('motorEfficiency', 'fraction', 'ESP motor efficiency.'),
  col('motorLoad', 'fraction', 'ESP motor load.'),
  col('pumpHead', 'length', 'ESP pump head.'),
  col('pumpAvgRate', 'liquidRate', 'Average pump rate over the calculation period.'),
];

/**
 * ESP operating-limit thresholds on `ESPLimits_String_Agent` (WF12):
 * the `esp`-prefixed engineering limits (motor temp/amps/power, pump delta-T,
 * fluid rates, intake/discharge pressures, head, free gas) plus their
 * monitoring `_active` flags, composed by spread with the shared
 * {@link STRING_LIMIT_COMMON} rate-limits + risk flags. The `esp` source
 * prefix is part of the real column name (only a subset carries it), so the
 * de-prefix axis is `none` (`prefix:''`); the `suffix` stays verbatim
 * (`esp*`), while the agent-facing `canonical` drops the `esp` prefix for
 * readability. The threshold columns are TimeSeries references (including the
 * `_active` flags, despite the source describing them as boolean).
 */
export const ESP_LIMITS: Shape = [
  col('maxMotorTemperature', 'temperature', 'Maximum allowable motor temperature.', { suffix: 'espMaxMotorTemp' }),
  col('maxMotorAmps', 'current', 'Maximum allowable motor current.', { suffix: 'espMaxMotorAmps' }),
  col('maxMotorPower', 'power', 'Maximum motor power limit.', { suffix: 'espMaxMotorPwr' }),
  col('maxPumpDeltaTemperature', 'temperature', 'Maximum temperature rise across the pump.', {
    suffix: 'espMaxPumpDeltaTemp',
  }),
  col('minFluidRate', 'liquidRate', 'Minimum fluid rate for stable operation.', { suffix: 'espMinFluidRate' }),
  col('maxFluidRate', 'liquidRate', 'Maximum fluid rate the pump can handle.', { suffix: 'espMaxFluidRate' }),
  col('minIntakePressure', 'pressureAbsolute', 'Minimum intake pressure limit.', { suffix: 'espMinIntakePressure' }),
  col('maxDischargePressure', 'pressureAbsolute', 'Maximum discharge pressure limit.', {
    suffix: 'espMaxDischargePressure',
  }),
  col('maxHead', 'length', 'Maximum head the pump can develop.', { suffix: 'espMaxHead' }),
  col('maxFreeGasInPump', 'unitless', 'Maximum free gas the pump can tolerate.', { suffix: 'espMaxFreeGasInPump' }),
  col(
    'maxMotorTemperature_active',
    'temperature',
    'Whether the max motor temperature limit is actively monitored for alarms.',
    { suffix: 'espMaxMotorTemp_active' },
  ),
  col('maxMotorAmps_active', 'current', 'Whether the max motor current limit is actively monitored for alarms.', {
    suffix: 'espMaxMotorAmps_active',
  }),
  col(
    'minIntakePressure_active',
    'pressureAbsolute',
    'Whether the min intake pressure limit is actively monitored for alarms.',
    { suffix: 'espMinIntakePressure_active' },
  ),
];

/**
 * Real-time ESP sensor readings on `ESPMeasurements_String_Agent`
 * (WF12): the ESP-specific pump readings (frequency, intake/discharge
 * pressure & temperature, motor temp, current, voltage, vibration), composed
 * by spread with the shared {@link STRING_SENSOR_COMMON} wellbore/choke
 * cluster. All columns are canonical-cased on the source (no `esp` prefix) —
 * bare passthrough (`suffix === canonical`, `prefix:''`). Each is a TimeSeries
 * reference.
 */
export const ESP_MEASUREMENTS: Shape = [
  col('pumpFrequency', 'frequency', 'ESP pump operating frequency.'),
  col('pumpIntakePressure', 'pressureAbsolute', 'Pressure at the pump intake.', { suffix: 'pumpIntakePress' }),
  col('pumpDischargePressure', 'pressureAbsolute', 'Pressure at the pump discharge.', { suffix: 'pumpDischargePress' }),
  col('pumpIntakeTemperature', 'temperature', 'Temperature at the pump intake.', { suffix: 'pumpIntakeTemp' }),
  col('pumpDischargeTemperature', 'temperature', 'Temperature at the pump discharge.', { suffix: 'pumpDischargeTemp' }),
  col('pumpMotorTemperature', 'temperature', 'ESP motor winding temperature.', { suffix: 'pumpMotorTemp' }),
  col('pumpCurrent', 'current', 'Motor current draw.'),
  col('pumpVoltage', 'voltage', 'Surface voltage supplied to the ESP.'),
  col('pumpVibration', 'unitless', 'Vibration sensor reading.'),
];

/**
 * Model-calculated gas-lift outputs on `GasLiftCalculations_String_Agent`
 * (WF14): GL analysis status, the GLIR variants (selected, measured,
 * network-simulation, choke-equation, allocated) and the calculated choke
 * size / discharge coefficient. The `gl`/`glir` source prefixes are part of
 * the real column names (mixed casing within the source), so the de-prefix
 * axis is `none` (`prefix:''`); `suffix` stays verbatim, and `canonical`
 * drops the `gl` prefix only on the two `glChoke*` columns for readability.
 * Each is a TimeSeries reference.
 */
export const GASLIFT_CALCULATIONS: Shape = [
  col('glqlAnalysisStatus', 'unitless', 'Status of the gas-lift rate calculation.'),
  col('glirSelected', 'gasRate', 'Selected / priority gas-lift injection rate.'),
  col('glirSelectedType', 'unitless', 'Source of the selected GLIR.'),
  col('glirMeasured', 'gasRate', 'Measured gas-lift injection rate from the flow meter.'),
  col('glirNetworkSimulation', 'gasRate', 'Gas-lift injection rate from the network simulation model.'),
  col('glirChokeEquation', 'gasRate', 'Gas-lift injection rate from the GL choke equation.'),
  col('glirAllocated', 'gasRate', 'Allocated gas-lift injection rate from daily allocation.'),
  col('chokeSizeCalc', 'chokeSize', 'Calculated GL choke size to achieve current GLIR.', { suffix: 'glChokeSizeCalc' }),
  col('chokeCdCalc', 'unitless', 'Calculated GL choke discharge coefficient.', { suffix: 'glChokeCdCalc' }),
];

/**
 * Gas-lift operating-limit thresholds on `GasLiftLimits_String_Agent`
 * (WF14): the `gl`-prefixed engineering limits (casing/wellhead/manifold
 * pressures, BHP target, temperatures) plus their monitoring `_active` flags
 * and the un-prefixed `maxWellHeadPressure`, composed by spread with the
 * shared {@link STRING_LIMIT_COMMON} rate-limits + risk flags. De-prefix axis
 * is `none` (`prefix:''`); `suffix` stays verbatim — including the SOURCE
 * TYPO `glMinManifolddPressure` (double-d), which must be authored exactly or
 * it resolves to `unknownCol`. There is no `glMaxWellHeadPressure` on the
 * source (only the un-prefixed `maxWellHeadPressure`). The threshold columns
 * are TimeSeries references.
 */
export const GASLIFT_LIMITS: Shape = [
  col('maxCasingHeadPressure', 'pressureAbsolute', 'Maximum allowable casing head pressure.', {
    suffix: 'glMaxCasingHeadPressure',
  }),
  col('minCasingHeadPressure', 'pressureAbsolute', 'Minimum casing head pressure for stable injection.', {
    suffix: 'glMinCasingHeadPressure',
  }),
  col('maxWellHeadTemperature', 'temperature', 'Maximum wellhead temperature limit.', { suffix: 'glMaxWellHeadTemp' }),
  col('minWellHeadPressure', 'pressureAbsolute', 'Minimum wellhead pressure for stable flow.', {
    suffix: 'glMinWellHeadPressure',
  }),
  col('maxWellHeadPressure', 'pressureAbsolute', 'Maximum wellhead pressure limit.'),
  col('maxManifoldPressure', 'pressureAbsolute', 'Maximum manifold pressure limit.', {
    suffix: 'glMaxManifoldPressure',
  }),
  col(
    'minManifoldPressure',
    'pressureAbsolute',
    'Minimum manifold pressure limit. The source column name carries a double-d typo (glMinManifolddPressure).',
    { suffix: 'glMinManifolddPressure' },
  ),
  col('minBHP', 'pressureAbsolute', 'Minimum bottom-hole pressure target.', { suffix: 'glMinBHP' }),
  col('maxLiftChokeDeltaTemperature', 'temperature', 'Maximum temperature drop across the gas-lift choke.', {
    suffix: 'glMaxLiftChokeDeltaTemp',
  }),
  col(
    'maxCasingHeadPressure_active',
    'pressureAbsolute',
    'Whether the max casing head pressure limit is actively monitored for alarms.',
    { suffix: 'glMaxCasingHeadPressure_active' },
  ),
  col(
    'minCasingHeadPressure_active',
    'pressureAbsolute',
    'Whether the min casing head pressure limit is actively monitored for alarms.',
    { suffix: 'glMinCasingHeadPressure_active' },
  ),
];

/**
 * Real-time gas-lift sensor readings on `GasLiftMeasurements_String_Agent`
 * (WF14): the GL-specific injection readings (injection rate, CV inlet
 * pressure, injection temperature, CV size), composed by spread with the
 * shared {@link STRING_SENSOR_COMMON} wellbore/choke cluster. The `gasLift`
 * source prefix is part of the real column names, so the de-prefix axis is
 * `none` (`prefix:''`); `suffix` stays verbatim, while `canonical` drops the
 * `gasLift` prefix for readability. Each is a TimeSeries reference.
 */
export const GASLIFT_MEASUREMENTS: Shape = [
  col('injectionRate', 'gasRate', 'Current gas-lift injection rate.', { suffix: 'gasLiftInjRate' }),
  col('flowCVInletPressure', 'pressureAbsolute', 'Inlet pressure at the gas-lift flow control valve.', {
    suffix: 'gasLiftFlowCVInletPress',
  }),
  col('injectionTemperature', 'temperature', 'Gas-lift injection temperature.', { suffix: 'gasLiftInjTemp' }),
  col('flowCVSize', 'unitless', 'Effective orifice / trim size of the gas-lift flow control valve.', {
    suffix: 'gasLiftFlowCVSize',
  }),
];
/**
 * ESP operating limits on `LiftTypeParameter_String` (the lift-type parameter
 * view): the ESP/pump-speed-bearing thresholds the artificial-lift agent limit
 * views omit — fluid-rate bounds, the inductor/PMM motor-speed (pump-speed)
 * limits, and the generated power-available ceiling. The source carries NO
 * scenario prefix, so the de-prefix axis is `none` (`prefix:''`): `suffix` is
 * the verbatim source column, while `canonical` drops the `esp` marker for
 * readability. Each is a TimeSeries reference.
 */
